import { useEffect, useState } from 'react';
import { sponsorsService, mediaUrl } from '../lib/api-service';
import type { Sponsor } from '../lib/types';
import { Award, ExternalLink, HeartHandshake, Instagram, Loader2 } from 'lucide-react';
import { DocumentMeta } from '../hooks/useDocumentMeta';

export default function SponsorsDirectory() {
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    sponsorsService.list()
      .then((data) => setSponsors(data.results))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--vivid-purple)]" />
      </div>
    );
  }

  const partners = sponsors.filter((s) => s.kind === 'partner');
  const sponsorsOnly = sponsors.filter((s) => s.kind === 'sponsor');

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <DocumentMeta
        title="Parteneri & Sponsori"
        description="Partenerii și sponsorii care susțin programul Alba Iulia — Capitala Tineretului din România 2026."
        url="/parteneri-sponsori"
      />
      <h1 className="mb-2 text-2xl font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
        Parteneri & Sponsori
      </h1>
      <p className="mb-6 text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
        Mulțumim partenerilor și sponsorilor care fac posibil programul Capitala Tineretului din România 2026.
      </p>

      {sponsors.length === 0 ? (
        <div className="flex flex-col items-center py-16 text-center">
          <Award className="mb-3 h-12 w-12 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
            Niciun partener sau sponsor afișat momentan.
          </p>
        </div>
      ) : (
        <div className="space-y-10">
          {partners.length > 0 && (
            <DirectorySection
              title="Parteneri"
              icon={HeartHandshake}
              items={partners}
              emptyIcon={HeartHandshake}
            />
          )}
          {sponsorsOnly.length > 0 && (
            <DirectorySection
              title="Sponsori"
              icon={Award}
              items={sponsorsOnly}
              emptyIcon={Award}
            />
          )}
        </div>
      )}
    </div>
  );
}

interface DirectorySectionProps {
  title: string;
  icon: typeof Award;
  items: Sponsor[];
  emptyIcon: typeof Award;
}

function DirectorySection({ title, icon: Icon, items, emptyIcon: EmptyIcon }: DirectorySectionProps) {
  return (
    <section>
      <h2 className="mb-3 flex items-center gap-2 text-lg font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
        <Icon className="h-5 w-5 text-[var(--vivid-purple)]" />
        {title}
      </h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((s) => {
          const card = (
            <div className="flex h-full items-center gap-4 rounded-2xl border bg-white p-4 transition-all hover:shadow-lg">
              {s.logo ? (
                <img src={mediaUrl(s.logo)} alt="" className="h-14 w-14 rounded-xl border object-cover" />
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[var(--vivid-yellow-green)]/30">
                  <EmptyIcon className="h-6 w-6 text-[var(--vivid-purple)]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="truncate text-sm font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
                  {s.name}
                </h3>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                  {s.website_url && (
                    <span className="inline-flex items-center gap-1">
                      <ExternalLink className="h-3 w-3" />
                      Vizitează site-ul
                    </span>
                  )}
                  {s.instagram && (
                    <span className="inline-flex items-center gap-1">
                      <Instagram className="h-3 w-3" />
                      @{s.instagram}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
          return s.website_url ? (
            <a key={s.id} href={s.website_url} target="_blank" rel="noopener noreferrer" className="block active:scale-[0.98]">
              {card}
            </a>
          ) : s.instagram ? (
            <a key={s.id} href={`https://instagram.com/${s.instagram}`} target="_blank" rel="noopener noreferrer" className="block active:scale-[0.98]">
              {card}
            </a>
          ) : (
            <div key={s.id}>{card}</div>
          );
        })}
      </div>
    </section>
  );
}
